import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as api from '../../services/api';

type SkillStatus = 'draft' | 'confirmed' | 'active' | 'deprecated';

const STATUS_COLOR: Record<SkillStatus, string> = {
  draft:      '#f59e0b',
  confirmed:  '#3b82f6',
  active:     '#10b981',
  deprecated: '#9ca3af',
};

// ── Styled primitives ──────────────────────────────────────────────────────────

const SectionTitle = styled.h2`
  font-size: 16px; font-weight: 600; color: #111827; margin: 0 0 16px 0;
`;

const Card = styled.div<{ highlight?: boolean }>`
  background: #fff;
  border: 1.5px solid ${p => p.highlight ? '#3b82f6' : '#e5e7eb'};
  border-radius: 12px;
  padding: 20px 22px;
  margin-bottom: 14px;
  transition: border-color 0.15s;
`;

const CardHeader = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: 10px;
`;

const SkillName = styled.span`
  font-weight: 600; font-size: 15px; color: #111827;
`;

const Badge = styled.span<{ color: string }>`
  font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 12px;
  background: ${p => p.color}22; color: ${p => p.color};
  border: 1px solid ${p => p.color}55;
  text-transform: uppercase; letter-spacing: 0.05em;
`;

const Meta = styled.div`
  font-size: 12px; color: #6b7280; margin-bottom: 12px; line-height: 1.7;
`;

const BtnRow = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap; align-items: center;
`;

const Btn = styled.button<{ variant?: 'primary' | 'success' | 'ghost' | 'danger' }>`
  padding: 7px 16px; font-size: 13px; font-weight: 500; border-radius: 7px; cursor: pointer;
  border: 1.5px solid ${p =>
    p.variant === 'primary' ? '#3b82f6' :
    p.variant === 'success' ? '#10b981' :
    p.variant === 'danger'  ? '#ef4444' : '#d1d5db'};
  background: ${p =>
    p.variant === 'primary' ? '#3b82f6' :
    p.variant === 'success' ? '#10b981' :
    p.variant === 'danger'  ? '#fef2f2' : '#f9fafb'};
  color: ${p =>
    p.variant === 'primary' ? '#fff' :
    p.variant === 'success' ? '#fff' :
    p.variant === 'danger'  ? '#ef4444' : '#374151'};
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const StatRow = styled.div`
  display: flex; gap: 20px; flex-wrap: wrap;
  background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px;
  padding: 12px 16px; margin: 12px 0;
`;

const Stat = styled.div`
  display: flex; flex-direction: column; align-items: center; min-width: 80px;
`;

const StatVal = styled.span`
  font-size: 22px; font-weight: 700; color: #065f46;
`;

const StatLabel = styled.span`
  font-size: 11px; color: #6b7280; margin-top: 2px; text-align: center;
`;

const ProgressBar = styled.div<{ pct: number }>`
  height: 6px; border-radius: 3px; background: #e5e7eb; margin: 10px 0;
  &::after {
    content: ''; display: block; height: 100%; border-radius: 3px;
    width: ${p => p.pct}%; background: #3b82f6;
    transition: width 0.4s ease;
  }
`;

const StatusMsg = styled.div<{ error?: boolean }>`
  font-size: 12px; color: ${p => p.error ? '#ef4444' : '#6b7280'};
  margin-top: 6px;
`;

const ContentBox = styled.pre`
  background: #1e293b; color: #e2e8f0; font-size: 12px; line-height: 1.55;
  padding: 14px 16px; border-radius: 8px; overflow-x: auto; white-space: pre-wrap;
  word-break: break-word; margin-top: 12px; max-height: 300px;
`;

const Divider = styled.hr`
  border: none; border-top: 1px solid #e5e7eb; margin: 28px 0;
`;

const TabBar = styled.div`
  display: flex; border-bottom: 2px solid #e5e7eb; margin-bottom: 24px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 22px; font-size: 14px; font-weight: 600; cursor: pointer;
  border: none; background: none; border-bottom: 2.5px solid ${p => p.$active ? '#3b82f6' : 'transparent'};
  color: ${p => p.$active ? '#3b82f6' : '#6b7280'};
  margin-bottom: -2px;
  &:hover { color: #111827; }
`;

const Row = styled.div`
  display: flex; gap: 12px; flex-wrap: wrap; align-items: flex-start;
`;

const Field = styled.div`
  flex: 1; min-width: 180px;
`;

const Label = styled.label`
  font-size: 12px; font-weight: 500; color: #374151; display: block; margin-bottom: 4px;
`;

const Input = styled.input`
  padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px;
  font-size: 13px; width: 100%; box-sizing: border-box;
`;

const Textarea = styled.textarea`
  width: 100%; min-height: 220px; font-family: 'JetBrains Mono', 'Menlo', monospace;
  font-size: 12px; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
  resize: vertical; box-sizing: border-box;
`;

const ErrMsg = styled.div`color: #ef4444; font-size: 13px; margin-top: 6px;`;
const OkMsg  = styled.div`color: #10b981; font-size: 13px; margin-top: 6px;`;

// ── Styled extras ──────────────────────────────────────────────────────────────

const FeedbackBox = styled.div`
  background: #fffbeb; border: 1.5px solid #fcd34d; border-radius: 10px;
  padding: 16px 18px; margin-top: 14px;
`;

const FeedbackTitle = styled.div`
  font-size: 13px; font-weight: 600; color: #92400e; margin-bottom: 10px;
`;

const SmTextarea = styled.textarea`
  width: 100%; font-family: 'JetBrains Mono', 'Menlo', monospace;
  font-size: 12px; padding: 8px 10px; border: 1px solid #d1d5db; border-radius: 6px;
  resize: vertical; box-sizing: border-box; min-height: 72px;
`;

const AnalysisBox = styled.div`
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px;
  padding: 14px 16px; margin-top: 12px; font-size: 13px; line-height: 1.6;
`;

const DiffBox = styled.pre`
  background: #0f172a; color: #a3e635; font-size: 11.5px; line-height: 1.5;
  padding: 12px 14px; border-radius: 8px; overflow-x: auto; white-space: pre;
  margin-top: 10px; max-height: 320px;
`;

const RunOptionsBox = styled.div`
  background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px;
  padding: 12px 14px; margin-top: 10px; display: flex; gap: 10px;
  align-items: center; flex-wrap: wrap;
`;

const SmInput = styled.input`
  padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 6px;
  font-size: 13px; width: 160px;
`;

// ── Per-skill state ────────────────────────────────────────────────────────────

interface ExploreState {
  phase: 'idle' | 'launching' | 'launched' | 'running' | 'done' | 'failed';
  jobId?: string;
  result?: api.PortalExploreResult;
  error?: string;
  launchMsg?: string;
}

interface RunState {
  phase: 'idle' | 'running' | 'done' | 'failed';
  jobId?: string;
  job?: api.CrawlerJob;
  error?: string;
}

interface FeedbackState {
  open: boolean;
  context: 'explore' | 'download';
  description: string;
  htmlSnippet: string;
  phase: 'idle' | 'loading' | 'done' | 'error';
  result?: api.SkillFeedbackResult;
  error?: string;
  applyBusy?: boolean;
  applied?: boolean;
}

// ── SkillCard ─────────────────────────────────────────────────────────────────

interface SkillCardProps {
  skill: api.PortalSkill;
  onStatusChange: (updated: api.PortalSkill) => void;
}

const SkillCard: React.FC<SkillCardProps> = ({ skill, onStatusChange }) => {
  const color = STATUS_COLOR[skill.status as SkillStatus] ?? '#9ca3af';

  const [exploreState, setExploreState] = useState<ExploreState>({ phase: 'idle' });
  const [runState, setRunState]         = useState<RunState>({ phase: 'idle' });
  const [showMd, setShowMd]             = useState(false);
  const [mdContent, setMdContent]       = useState<string | null>(null);
  const [mdLoading, setMdLoading]       = useState(false);
  const [confirmBusy, setConfirmBusy]   = useState(false);
  const [deprecateBusy, setDeprecateBusy] = useState(false);
  const [pageInput, setPageInput]       = useState('1');  // "1" | "1,3,5" | "all"
  const [feedback, setFeedback]         = useState<FeedbackState>({
    open: false, context: 'explore', description: '', htmlSnippet: '', phase: 'idle',
  });
  const [elapsed, setElapsed]           = useState(0);
  const elapsedTimer                    = useRef<ReturnType<typeof setInterval> | null>(null);

  const exploreTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const runTimer     = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Explore polling ────────────────────────────────────────────────────────
  const pollExplore = useCallback((jobId: string) => {
    exploreTimer.current = setInterval(async () => {
      try {
        const res = await api.getExploreStatus(jobId);
        if (res.status === 'completed') {
          clearInterval(exploreTimer.current!);
          setExploreState({ phase: 'done', jobId, result: res });
        } else if (res.status === 'failed') {
          clearInterval(exploreTimer.current!);
          setExploreState({ phase: 'failed', jobId, error: res.error || 'Explore failed' });
        } else {
          setExploreState(p => ({ ...p, result: res }));
        }
      } catch (e: any) {
        clearInterval(exploreTimer.current!);
        setExploreState({ phase: 'failed', error: e?.message });
      }
    }, 2000);
  }, []);

  const launchChrome = async () => {
    setExploreState({ phase: 'launching' });
    try {
      const res = await api.launchChrome(skill.portal_url);
      if (res.already_running) {
        setExploreState({ phase: 'launched', launchMsg: 'Chrome already running with CDP.' });
      } else {
        setExploreState({ phase: 'launched', launchMsg: res.message });
      }
    } catch (e: any) {
      setExploreState({ phase: 'failed', error: e?.message });
    }
  };

  const startExplore = async () => {
    setExploreState({ phase: 'running' });
    setRunState({ phase: 'idle' });
    try {
      const res = await api.explorePortal(skill.name);
      if (!res.success || !res.job_id) throw new Error(res.error || 'No job ID');
      setExploreState({ phase: 'running', jobId: res.job_id });
      pollExplore(res.job_id);
    } catch (e: any) {
      setExploreState({ phase: 'failed', error: e?.message });
    }
  };

  // ── Run polling ────────────────────────────────────────────────────────────
  const pollRun = useCallback((jobId: string) => {
    runTimer.current = setInterval(async () => {
      try {
        const res = await api.getCrawlerJobStatus(jobId);
        if (res.status === 'completed') {
          clearInterval(runTimer.current!);
          setRunState({ phase: 'done', jobId, job: res });
        } else if (res.status === 'failed') {
          clearInterval(runTimer.current!);
          setRunState({ phase: 'failed', jobId, job: res, error: res.error || 'Run failed' });
        } else {
          setRunState(p => ({ ...p, job: res }));
        }
      } catch (e: any) {
        clearInterval(runTimer.current!);
        setRunState({ phase: 'failed', error: e?.message });
      }
    }, 3000);
  }, []);

  const parsePageInput = (): number[] | 'all' => {
    const v = pageInput.trim().toLowerCase();
    if (!v || v === 'all') return 'all';
    return v.split(/[,\s]+/).map(s => parseInt(s, 10)).filter(n => !isNaN(n) && n > 0);
  };

  const startRun = async (dryRun = false) => {
    setRunState({ phase: 'running' });
    const pages = parsePageInput();
    try {
      const res = await api.startCrawlerJob(skill.name, { guided_mode: true, pages, dry_run: dryRun });
      if (!res.success || !res.job_id) throw new Error(res.error || 'No job ID');
      setRunState({ phase: 'running', jobId: res.job_id });
      pollRun(res.job_id);
    } catch (e: any) {
      setRunState({ phase: 'failed', error: e?.message });
    }
  };

  // ── Confirm ────────────────────────────────────────────────────────────────
  const confirmSkill = async () => {
    setConfirmBusy(true);
    try {
      const res = await api.updatePortalSkillStatus(skill.name, 'active');
      onStatusChange(res.skill);
    } catch (e: any) {
      alert(`Confirm failed: ${e?.message}`);
    } finally {
      setConfirmBusy(false);
    }
  };

  const deprecateSkill = async () => {
    setDeprecateBusy(true);
    try {
      const res = await api.updatePortalSkillStatus(skill.name, 'deprecated');
      onStatusChange(res.skill);
    } catch (e: any) {
      alert(`Deprecate failed: ${e?.message}`);
    } finally {
      setDeprecateBusy(false);
    }
  };

  // ── Feedback ───────────────────────────────────────────────────────────────
  const submitFeedback = async () => {
    setFeedback(p => ({ ...p, phase: 'loading', result: undefined, error: undefined }));
    setElapsed(0);
    elapsedTimer.current = setInterval(() => setElapsed(s => s + 1), 1000);
    try {
      const res = await api.submitSkillFeedback(skill.name, {
        context: feedback.context,
        description: feedback.description,
        html_snippet: feedback.htmlSnippet || undefined,
        explore_stats: exploreState.result
          ? {
              estimated_patients: exploreState.result.estimated_patients,
              total_pages: exploreState.result.total_pages,
              patients_per_page: exploreState.result.patients_per_page,
              message: exploreState.result.message,
            }
          : undefined,
      });
      setFeedback(p => ({ ...p, phase: 'done', result: res.result }));
    } catch (e: any) {
      setFeedback(p => ({ ...p, phase: 'error', error: e?.message }));
    } finally {
      if (elapsedTimer.current) { clearInterval(elapsedTimer.current); elapsedTimer.current = null; }
    }
  };

  const applyUpdatedMarkdown = async () => {
    if (!feedback.result?.updated_markdown) return;
    setFeedback(p => ({ ...p, applyBusy: true }));
    try {
      await api.createPortalSkill({
        name: skill.name,
        display_name: skill.display_name,
        portal_url: skill.portal_url,
        s3_key: skill.s3_key,
        content: feedback.result!.updated_markdown,
      });
      setMdContent(feedback.result!.updated_markdown);
      setFeedback(p => ({ ...p, applyBusy: false, applied: true }));
    } catch (e: any) {
      alert(`Apply failed: ${e?.message}`);
      setFeedback(p => ({ ...p, applyBusy: false }));
    }
  };

  // ── View MD ────────────────────────────────────────────────────────────────
  const toggleMd = async () => {
    if (showMd) { setShowMd(false); return; }
    if (mdContent !== null) { setShowMd(true); return; }
    setMdLoading(true);
    try {
      const s = await api.getPortalSkill(skill.name);
      setMdContent(s.content ?? '(empty)');
    } catch (e: any) {
      setMdContent(`Error: ${e?.message}`);
    } finally {
      setMdLoading(false);
      setShowMd(true);
    }
  };

  // cleanup on unmount
  useEffect(() => () => {
    if (exploreTimer.current) clearInterval(exploreTimer.current);
    if (runTimer.current)     clearInterval(runTimer.current);
    if (elapsedTimer.current) clearInterval(elapsedTimer.current);
  }, []);

  const { result: exploreResult } = exploreState;
  const runJob = runState.job;
  const runPct = runJob ? Math.round((runJob.current_page / Math.max(runJob.total_pages, 1)) * 100) : 0;

  const canExplore  = skill.status !== 'deprecated';
  const canConfirm  = exploreState.phase === 'done' && skill.status !== 'active';
  const canRun      = skill.status === 'active' && runState.phase === 'idle';

  return (
    <Card highlight={exploreState.phase === 'done' && skill.status !== 'active'}>
      <CardHeader>
        <SkillName>{skill.display_name}</SkillName>
        <Badge color={color}>{skill.status}</Badge>
        <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>v{skill.version}</span>
      </CardHeader>

      <Meta>
        <div><strong>Portal:</strong> {skill.portal_url}</div>
        <div><strong>S3:</strong> s3://{skill.s3_bucket}/{skill.s3_key}</div>
      </Meta>

      {/* ── Step 1: Launch Chrome ── */}
      {exploreState.phase === 'idle' && (
        <BtnRow>
          {canExplore && (
            <Btn variant="primary" onClick={launchChrome}>
              Launch Chrome + log in
            </Btn>
          )}
          {skill.status === 'active' && (
            <Btn variant="ghost" onClick={deprecateSkill} disabled={deprecateBusy}>
              {deprecateBusy ? '...' : 'Deprecate'}
            </Btn>
          )}
          <Btn variant="ghost" onClick={toggleMd} disabled={mdLoading}>
            {mdLoading ? 'Loading...' : showMd ? 'Hide MD' : 'View MD'}
          </Btn>
        </BtnRow>
      )}

      {exploreState.phase === 'launching' && (
        <BtnRow><Btn variant="ghost" disabled>Launching Chrome...</Btn></BtnRow>
      )}

      {/* ── Step 2: Logged in? → Explore ── */}
      {exploreState.phase === 'launched' && (
        <>
          <StatusMsg style={{ marginBottom: 10, color: '#059669' }}>
            {exploreState.launchMsg}
          </StatusMsg>
          <BtnRow>
            <Btn variant="primary" onClick={startExplore}>Explore portal</Btn>
            <Btn variant="ghost" onClick={toggleMd} disabled={mdLoading}>
              {showMd ? 'Hide MD' : 'View MD'}
            </Btn>
          </BtnRow>
          <StatusMsg>Log in to the portal in Chrome, then click Explore.</StatusMsg>
        </>
      )}

      {exploreState.phase === 'running' && (
        <>
          <BtnRow><Btn variant="ghost" disabled>Exploring...</Btn></BtnRow>
          <StatusMsg>{exploreState.result?.message || 'Connecting to Chrome and reading portal...'}</StatusMsg>
        </>
      )}

      {exploreState.phase === 'failed' && (
        <>
          <BtnRow>
            <Btn variant="primary" onClick={launchChrome}>Launch Chrome</Btn>
            <Btn variant="ghost" onClick={toggleMd} disabled={mdLoading}>
              {showMd ? 'Hide MD' : 'View MD'}
            </Btn>
          </BtnRow>
          <StatusMsg error>{exploreState.error}</StatusMsg>
        </>
      )}

      {/* ── Step 2: Review results + Confirm ── */}
      {exploreState.phase === 'done' && exploreResult && (
        <>
          <StatRow>
            <Stat>
              <StatVal>{exploreResult.estimated_patients.toLocaleString()}</StatVal>
              <StatLabel>Total records</StatLabel>
            </Stat>
            <Stat>
              <StatVal>{exploreResult.total_pages}</StatVal>
              <StatLabel>Pages</StatLabel>
            </Stat>
            <Stat>
              <StatVal>{exploreResult.patients_per_page}</StatVal>
              <StatLabel>Per page</StatLabel>
            </Stat>
            <Stat>
              <StatVal style={{ color: '#065f46', fontSize: 20 }}>✓</StatVal>
              <StatLabel>Selectors OK</StatLabel>
            </Stat>
          </StatRow>
          {exploreResult.message && (
            <StatusMsg style={{ marginBottom: 10 }}>{exploreResult.message}</StatusMsg>
          )}
          <BtnRow>
            {canConfirm && (
              <Btn variant="success" onClick={confirmSkill} disabled={confirmBusy}>
                {confirmBusy ? 'Confirming...' : 'Confirm & Activate skill'}
              </Btn>
            )}
            <Btn variant="ghost" onClick={startExplore}>Re-explore</Btn>
            <Btn variant="ghost" onClick={() => setFeedback(p => ({ ...p, open: !p.open, context: 'explore' }))}>
              {feedback.open && feedback.context === 'explore' ? 'Hide feedback' : 'Results wrong? Give feedback'}
            </Btn>
            <Btn variant="ghost" onClick={toggleMd} disabled={mdLoading}>
              {showMd ? 'Hide MD' : 'View MD'}
            </Btn>
          </BtnRow>

          {/* ── Feedback panel (explore context) ── */}
          {feedback.open && feedback.context === 'explore' && (
            <FeedbackBox>
              <FeedbackTitle>
                {feedback.context === 'download'
                  ? 'Give Claude a hint about the download button'
                  : 'Tell Claude what\'s wrong with these explore results'}
              </FeedbackTitle>
              <Label style={{ marginBottom: 4 }}>What's incorrect? (required)</Label>
              <SmTextarea
                placeholder={feedback.context === 'download'
                  ? "e.g. 'The download button has class btn-download, not data-cy=download-button. Clicking it directly downloads without a menu.'"
                  : "e.g. '279 is the total lab orders, not patients. Pages should be 279, not 12.'"}
                value={feedback.description}
                onChange={e => setFeedback(p => ({ ...p, description: e.target.value }))}
                style={{ marginBottom: 10 }}
              />
              <Label style={{ marginBottom: 4 }}>HTML snippet from browser inspector (optional)</Label>
              <SmTextarea
                placeholder='e.g. <p class="MuiTablePagination-caption">1-25 of 279</p>'
                value={feedback.htmlSnippet}
                onChange={e => setFeedback(p => ({ ...p, htmlSnippet: e.target.value }))}
                style={{ marginBottom: 10, minHeight: 56 }}
              />
              <BtnRow>
                <Btn
                  variant="primary"
                  onClick={submitFeedback}
                  disabled={!feedback.description || feedback.phase === 'loading'}
                >
                  {feedback.phase === 'loading' ? `Asking Claude... (${elapsed}s)` : 'Ask Claude to analyze'}
                </Btn>
              </BtnRow>

              {feedback.phase === 'error' && (
                <StatusMsg error style={{ marginTop: 8 }}>{feedback.error}</StatusMsg>
              )}

              {feedback.phase === 'done' && feedback.result && (
                <AnalysisBox>
                  <div style={{ fontWeight: 600, marginBottom: 6, color: '#1e293b' }}>Claude's analysis</div>
                  <div style={{ marginBottom: 10 }}>{feedback.result.analysis}</div>

                  {Object.keys(feedback.result.corrected_interpretation).length > 0 && (
                    <>
                      <div style={{ fontWeight: 600, marginBottom: 4, color: '#1e293b' }}>Corrected field meanings</div>
                      <ul style={{ margin: '0 0 10px 18px', padding: 0 }}>
                        {Object.entries(feedback.result.corrected_interpretation).map(([k, v]) => (
                          <li key={k}><strong>{k}</strong>: {v}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {feedback.result.selector_changes.length > 0 && (
                    <>
                      <div style={{ fontWeight: 600, marginBottom: 4, color: '#1e293b' }}>Selector / code changes</div>
                      {feedback.result.selector_changes.map((c, i) => (
                        <div key={i} style={{ marginBottom: 6, fontSize: 12 }}>
                          <span style={{ color: '#ef4444' }}>- {c.old}</span><br />
                          <span style={{ color: '#10b981' }}>+ {c.new}</span><br />
                          <span style={{ color: '#6b7280' }}>{c.reason}</span>
                        </div>
                      ))}
                    </>
                  )}

                  {feedback.result.code_diff && (
                    <>
                      <div style={{ fontWeight: 600, marginBottom: 4, color: '#1e293b' }}>Code fix</div>
                      <DiffBox>{feedback.result.code_diff}</DiffBox>
                    </>
                  )}

                  {feedback.result.updated_markdown && (
                    <BtnRow style={{ marginTop: 12 }}>
                      <Btn
                        variant="primary"
                        onClick={applyUpdatedMarkdown}
                        disabled={!!feedback.applyBusy || !!feedback.applied}
                      >
                        {feedback.applyBusy ? 'Saving...' : feedback.applied ? 'Markdown updated ✓' : 'Apply updated Markdown to S3'}
                      </Btn>
                    </BtnRow>
                  )}
                </AnalysisBox>
              )}
            </FeedbackBox>
          )}
        </>
      )}

      {/* ── Feedback panel (download context) — shown outside explore-done block ── */}
      {feedback.open && feedback.context === 'download' && (
        <FeedbackBox>
          <FeedbackTitle>Give Claude a hint about the download button</FeedbackTitle>
          <Label style={{ marginBottom: 4 }}>What's wrong? (required)</Label>
          <SmTextarea
            placeholder="e.g. 'The download button has class btn-download, not data-cy=download-button. Clicking it directly downloads without a menu.'"
            value={feedback.description}
            onChange={e => setFeedback(p => ({ ...p, description: e.target.value }))}
            style={{ marginBottom: 10 }}
          />
          <Label style={{ marginBottom: 4 }}>HTML from browser inspector (paste the download button element)</Label>
          <SmTextarea
            placeholder='e.g. <button class="btn-download" data-testid="download-pdf">Download PDF</button>'
            value={feedback.htmlSnippet}
            onChange={e => setFeedback(p => ({ ...p, htmlSnippet: e.target.value }))}
            style={{ marginBottom: 10, minHeight: 56 }}
          />
          <BtnRow>
            <Btn
              variant="primary"
              onClick={submitFeedback}
              disabled={!feedback.description || feedback.phase === 'loading'}
            >
              {feedback.phase === 'loading' ? `Asking Claude... (${elapsed}s)` : 'Ask Claude to analyze'}
            </Btn>
            <Btn variant="ghost" onClick={() => setFeedback(p => ({ ...p, open: false }))}>Cancel</Btn>
          </BtnRow>
          {feedback.phase === 'error' && (
            <StatusMsg error style={{ marginTop: 8 }}>{feedback.error}</StatusMsg>
          )}
          {feedback.phase === 'done' && feedback.result && (
            <AnalysisBox>
              <div style={{ fontWeight: 600, marginBottom: 6, color: '#1e293b' }}>Claude's analysis</div>
              <div style={{ marginBottom: 10 }}>{feedback.result.analysis}</div>
              {feedback.result.selector_changes.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 4, color: '#1e293b' }}>Selector fixes</div>
                  {feedback.result.selector_changes.map((c, i) => (
                    <div key={i} style={{ marginBottom: 6, fontSize: 12 }}>
                      <span style={{ color: '#ef4444' }}>- {c.old}</span><br />
                      <span style={{ color: '#10b981' }}>+ {c.new}</span><br />
                      <span style={{ color: '#6b7280' }}>{c.reason}</span>
                    </div>
                  ))}
                </>
              )}
              {feedback.result.code_diff && (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 4, color: '#1e293b' }}>Code fix</div>
                  <DiffBox>{feedback.result.code_diff}</DiffBox>
                </>
              )}
              {feedback.result.updated_markdown && (
                <BtnRow style={{ marginTop: 12 }}>
                  <Btn
                    variant="primary"
                    onClick={applyUpdatedMarkdown}
                    disabled={!!feedback.applyBusy || !!feedback.applied}
                  >
                    {feedback.applyBusy ? 'Saving...' : feedback.applied ? 'Markdown updated ✓' : 'Apply updated Markdown to S3'}
                  </Btn>
                </BtnRow>
              )}
            </AnalysisBox>
          )}
        </FeedbackBox>
      )}

      {/* ── Run options panel — shown when active and no run in progress ── */}
      {skill.status === 'active' && runState.phase === 'idle' && (
        <RunOptionsBox>
          <span style={{ fontSize: 12, color: '#0369a1', fontWeight: 500, whiteSpace: 'nowrap' }}>
            Pages:
          </span>
          <SmInput
            value={pageInput}
            onChange={e => setPageInput(e.target.value)}
            placeholder="1  or  1,3,5  or  all"
          />
          {[1, 2, 3].map(n => (
            <Btn key={n} variant="ghost" onClick={() => setPageInput(String(n))}
              style={{ padding: '5px 10px', fontSize: 12,
                       background: pageInput === String(n) ? '#e0f2fe' : undefined,
                       borderColor: pageInput === String(n) ? '#38bdf8' : undefined }}>
              Pg {n}
            </Btn>
          ))}
          <Btn variant="ghost" onClick={() => setPageInput('all')}
            style={{ padding: '5px 10px', fontSize: 12,
                     background: pageInput === 'all' ? '#e0f2fe' : undefined,
                     borderColor: pageInput === 'all' ? '#38bdf8' : undefined }}>
            All
          </Btn>
          <Btn variant="ghost" onClick={() => startRun(true)}
            style={{ padding: '5px 10px', fontSize: 12, color: '#7c3aed', borderColor: '#7c3aed' }}>
            Dry Run
          </Btn>
          <Btn variant="success" onClick={() => startRun(false)}>
            Run{pageInput === 'all' ? ' all' : ` p.${pageInput}`}
          </Btn>
        </RunOptionsBox>
      )}

      {/* ── Step 3: Run progress ── */}
      {(runState.phase === 'running' || runState.phase === 'done' || runState.phase === 'failed') && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151', marginBottom: 4 }}>
            <span>
              {runState.phase === 'done'   ? `Done — ${runJob?.downloaded_files ?? 0} files downloaded` :
               runState.phase === 'failed' ? `Failed: ${runState.error}` :
               runJob?.message || 'Downloading...'}
            </span>
            <span>{runPct}%</span>
          </div>
          <ProgressBar pct={runState.phase === 'done' ? 100 : runPct} />
          {runJob && (
            <StatusMsg>
              Page {runJob.current_page}/{runJob.total_pages} · {runJob.downloaded_files} files
              {runJob.error && <span style={{ color: '#ef4444' }}> — {runJob.error}</span>}
            </StatusMsg>
          )}
          {(runState.phase === 'done' || runState.phase === 'failed') && (
            <BtnRow style={{ marginTop: 10 }}>
              <Btn variant="ghost" onClick={() => setRunState({ phase: 'idle' })}>
                {runState.phase === 'done' ? 'Run again' : 'Try again'}
              </Btn>
              {(runState.phase === 'failed' || (runState.phase === 'done' && (runJob?.downloaded_files ?? 0) === 0)) && (
                <Btn variant="ghost" onClick={() => setFeedback(p => ({
                  ...p,
                  open: true,
                  context: 'download',
                  description: runState.phase === 'failed'
                    ? `Run failed: ${runState.error}`
                    : '0 files were downloaded. The download button selector may be wrong.',
                }))}>
                  Selectors wrong? Give hint
                </Btn>
              )}
            </BtnRow>
          )}
        </div>
      )}

      {showMd && mdContent && <ContentBox>{mdContent}</ContentBox>}
    </Card>
  );
};

// ── New Skill Form ─────────────────────────────────────────────────────────────

interface NewSkillFormProps {
  onSaved: () => void;
  onCancel: () => void;
}

const NewSkillForm: React.FC<NewSkillFormProps> = ({ onSaved, onCancel }) => {
  const [name, setName]       = useState('');
  const [display, setDisplay] = useState('');
  const [url, setUrl]         = useState('');
  const [s3Key, setS3Key]     = useState('');
  const [content, setContent] = useState('');
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');

  const onNameChange = (v: string) => {
    setName(v);
    setS3Key(`mcp_agent/skills/${v}.md`);
    if (!display) setDisplay(v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
  };

  const submit = async () => {
    setErr('');
    if (!name || !display || !url || !s3Key || !content) { setErr('All fields required.'); return; }
    setBusy(true);
    try {
      await api.createPortalSkill({ name, display_name: display, portal_url: url, s3_key: s3Key, content });
      onSaved();
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card highlight style={{ marginBottom: 24 }}>
      <SectionTitle>New Skill</SectionTitle>
      <Row style={{ marginBottom: 12 }}>
        <Field>
          <Label>Skill name (snake_case)</Label>
          <Input value={name} onChange={e => onNameChange(e.target.value)} placeholder="e.g. rupa_health" />
        </Field>
        <Field>
          <Label>Display name</Label>
          <Input value={display} onChange={e => setDisplay(e.target.value)} placeholder="e.g. Rupa Health" />
        </Field>
      </Row>
      <Row style={{ marginBottom: 12 }}>
        <Field style={{ flex: 2 }}>
          <Label>Portal URL</Label>
          <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..." />
        </Field>
        <Field>
          <Label>S3 key</Label>
          <Input value={s3Key} onChange={e => setS3Key(e.target.value)} />
        </Field>
      </Row>
      <Label>Skill content (Markdown)</Label>
      <Textarea
        value={content}
        onChange={e => setContent(e.target.value)}
        placeholder={'# Skill: Portal Name\n\n## Portal\n- URL: ...\n\n## Pagination\n...'}
      />
      <BtnRow style={{ marginTop: 12 }}>
        <Btn variant="primary" onClick={submit} disabled={busy}>{busy ? 'Saving...' : 'Save to S3 + RDS'}</Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
        {err && <ErrMsg>{err}</ErrMsg>}
      </BtnRow>
    </Card>
  );
};

// ── AdminSkills (main) ────────────────────────────────────────────────────────

// ── Self-Learning UI ──────────────────────────────────────────────────────────

const LEARN_STATUS_COLOR: Record<string, string> = {
  pending:    '#9ca3af',
  exploring:  '#3b82f6',
  attempting: '#8b5cf6',
  drafting:   '#f59e0b',
  verifying:  '#06b6d4',
  done:       '#10b981',
  failed:     '#ef4444',
  cancelled:  '#9ca3af',
};

const IN_PROGRESS_STATUSES = new Set(['exploring', 'attempting', 'drafting', 'verifying']);

const LearnRequestCard: React.FC<{
  req: api.SkillLearnRequest;
  onRefresh: () => void;
}> = ({ req, onRefresh }) => {
  const color = LEARN_STATUS_COLOR[req.status] ?? '#9ca3af';
  const log   = req.agent_log ?? [];

  return (
    <Card>
      <CardHeader>
        <Badge color={color}>{req.status}</Badge>
        <SkillName>{req.portal_name}</SkillName>
        <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 'auto' }}>
          {new Date(req.created_at).toLocaleString()}
        </span>
      </CardHeader>

      <Meta>
        <span style={{ color: '#374151' }}>Goal: </span>{req.goal}<br />
        <span style={{ color: '#374151' }}>URL: </span>
        <a href={req.portal_url} target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>
          {req.portal_url}
        </a>
      </Meta>

      {log.length > 0 && (
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
          padding: '10px 14px', marginBottom: 12, maxHeight: 160, overflowY: 'auto',
          fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6,
        }}>
          {log.map((entry, i) => (
            <div key={i} style={{ color: LEARN_STATUS_COLOR[entry.phase] ?? '#374151' }}>
              <span style={{ color: '#94a3b8', marginRight: 8 }}>
                {new Date(entry.ts).toLocaleTimeString()}
              </span>
              [{entry.phase}] {entry.message}
            </div>
          ))}
        </div>
      )}

      {req.error && (
        <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 10 }}>
          Error: {req.error}
        </div>
      )}

      <BtnRow>
        {req.result_skill_name && (
          <Badge color="#10b981" style={{ padding: '4px 10px', fontSize: 12 }}>
            Draft skill: {req.result_skill_name}
          </Badge>
        )}
        {IN_PROGRESS_STATUSES.has(req.status) && (
          <Btn variant="ghost" onClick={onRefresh} style={{ marginLeft: 'auto' }}>
            Refresh
          </Btn>
        )}
      </BtnRow>
    </Card>
  );
};

const LearnNewSkillForm: React.FC<{
  onSubmitted: (req: api.SkillLearnRequest) => void;
  onCancel: () => void;
}> = ({ onSubmitted, onCancel }) => {
  const [portalName, setPortalName] = useState('');
  const [portalUrl,  setPortalUrl]  = useState('');
  const [goal,       setGoal]       = useState('');
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState('');

  const submit = async () => {
    setErr('');
    if (!portalName.trim() || !portalUrl.trim() || !goal.trim()) {
      setErr('All fields are required.'); return;
    }
    setSaving(true);
    try {
      const res = await api.createLearnRequest({ portal_name: portalName, portal_url: portalUrl, goal });
      onSubmitted(res.request);
    } catch (e: any) {
      setErr(e?.message || 'Failed to start learning.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', fontSize: 13, borderRadius: 7,
    border: '1.5px solid #d1d5db', outline: 'none', boxSizing: 'border-box', marginBottom: 10,
  };

  return (
    <Card highlight>
      <SectionTitle style={{ marginBottom: 14 }}>Teach Agent a New Portal Skill</SectionTitle>
      <label style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Portal name</label>
      <input style={inputStyle} placeholder="e.g. Vibrant Wellness"
        value={portalName} onChange={e => setPortalName(e.target.value)} />
      <label style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Portal URL</label>
      <input style={inputStyle} placeholder="https://portal.vibrantwellness.com"
        value={portalUrl} onChange={e => setPortalUrl(e.target.value)} />
      <label style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>Learning goal</label>
      <textarea style={{ ...inputStyle, height: 72, resize: 'vertical' }}
        placeholder="e.g. Download patient lab result PDFs for a given page of patients"
        value={goal} onChange={e => setGoal(e.target.value)} />
      {err && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <BtnRow>
        <Btn variant="primary" onClick={submit} disabled={saving}>
          {saving ? 'Starting...' : 'Start Learning'}
        </Btn>
        <Btn variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Btn>
      </BtnRow>
    </Card>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface Props { embedded?: boolean }

const AdminSkills: React.FC<Props> = () => {
  const [skills, setSkills]           = useState<api.PortalSkill[]>([]);
  const [loading, setLoading]         = useState(true);
  const [err, setErr]                 = useState('');
  const [showForm, setShowForm]       = useState(false);
  const [showLearnForm, setShowLearnForm] = useState(false);
  const [learnRequests, setLearnRequests] = useState<api.SkillLearnRequest[]>([]);
  const [activeTab, setActiveTab]     = useState<'active' | 'new'>('active');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.listPortalSkills(), api.listLearnRequests()])
      .then(([skillsRes, learnRes]) => {
        setSkills(skillsRes.skills);
        setLearnRequests(learnRes.requests);
      })
      .catch(e => setErr(e?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while any learn request is in progress
  useEffect(() => {
    const hasActive = learnRequests.some(r => IN_PROGRESS_STATUSES.has(r.status));
    if (!hasActive) return;
    const t = setInterval(async () => {
      const active = learnRequests.filter(r => IN_PROGRESS_STATUSES.has(r.status));
      const updated = await Promise.all(active.map(r => api.getLearnRequest(r.id)));
      setLearnRequests(prev =>
        prev.map(r => updated.find(u => u.id === r.id) ?? r)
      );
    }, 3000);
    return () => clearInterval(t);
  }, [learnRequests]);

  const handleStatusChange = (updated: api.PortalSkill) => {
    setSkills(p => p.map(s => s.name === updated.name ? updated : s));
  };

  if (loading) return <div style={{ color: '#6b7280', padding: 8 }}>Loading...</div>;
  if (err)     return <div style={{ color: '#ef4444', padding: 8 }}>{err}</div>;

  const activeSkills = skills.filter(s => s.status === 'active');
  const newSkills    = skills.filter(s => s.status !== 'active');
  const inProgress   = learnRequests.filter(r => IN_PROGRESS_STATUSES.has(r.status));
  const history      = learnRequests.filter(r => !IN_PROGRESS_STATUSES.has(r.status));

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ marginBottom: 20 }}>
        <SectionTitle style={{ margin: 0 }}>MCP Agent Center</SectionTitle>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
          Explore a portal → review stats → confirm → activate
        </div>
      </div>

      {/* ── Tabs ── */}
      <TabBar>
        <Tab $active={activeTab === 'active'} onClick={() => setActiveTab('active')}>
          Active Skills {activeSkills.length > 0 && `(${activeSkills.length})`}
        </Tab>
        <Tab $active={activeTab === 'new'} onClick={() => setActiveTab('new')}>
          New Skills {(newSkills.length + learnRequests.length) > 0 && `(${newSkills.length + learnRequests.length})`}
        </Tab>
      </TabBar>

      {/* ── Tab 1: Active Skills ── */}
      {activeTab === 'active' && (
        <>
          {activeSkills.length === 0
            ? <div style={{ color: '#6b7280' }}>No active skills yet. Train a skill in the New Skills tab.</div>
            : activeSkills.map(skill => (
                <SkillCard key={skill.name} skill={skill} onStatusChange={handleStatusChange} />
              ))
          }
        </>
      )}

      {/* ── Tab 2: New Skills ── */}
      {activeTab === 'new' && (
        <>
          {/* Action buttons */}
          <BtnRow style={{ marginBottom: 20 }}>
            {!showLearnForm && (
              <Btn variant="success" onClick={() => { setShowLearnForm(true); setShowForm(false); }}>
                ✦ Teach New Skill
              </Btn>
            )}
            {!showForm && (
              <Btn variant="primary" onClick={() => { setShowForm(true); setShowLearnForm(false); }}>
                + Add Skill
              </Btn>
            )}
          </BtnRow>

          {/* Learn new skill form */}
          {showLearnForm && (
            <LearnNewSkillForm
              onSubmitted={req => {
                setLearnRequests(prev => [req, ...prev]);
                setShowLearnForm(false);
              }}
              onCancel={() => setShowLearnForm(false)}
            />
          )}

          {/* Manual skill form */}
          {showForm && (
            <NewSkillForm
              onSaved={() => { setShowForm(false); load(); }}
              onCancel={() => setShowForm(false)}
            />
          )}

          {/* Skills in draft/confirmed (not yet active) */}
          {newSkills.length > 0 && (
            <>
              <SectionTitle style={{ marginTop: 8 }}>In Development</SectionTitle>
              {newSkills.map(skill => (
                <SkillCard key={skill.name} skill={skill} onStatusChange={handleStatusChange} />
              ))}
            </>
          )}

          {/* Ongoing learning sessions */}
          {inProgress.length > 0 && (
            <>
              <SectionTitle style={{ marginTop: 8 }}>Currently Learning</SectionTitle>
              {inProgress.map(req => (
                <LearnRequestCard
                  key={req.id}
                  req={req}
                  onRefresh={() =>
                    api.getLearnRequest(req.id).then(updated =>
                      setLearnRequests(prev => prev.map(r => r.id === req.id ? updated : r))
                    )
                  }
                />
              ))}
            </>
          )}

          {/* Past learning sessions */}
          {history.length > 0 && (
            <>
              <SectionTitle style={{ marginTop: 24 }}>Learning History</SectionTitle>
              {history.map(req => (
                <LearnRequestCard
                  key={req.id}
                  req={req}
                  onRefresh={() =>
                    api.getLearnRequest(req.id).then(updated =>
                      setLearnRequests(prev => prev.map(r => r.id === req.id ? updated : r))
                    )
                  }
                />
              ))}
            </>
          )}

          {newSkills.length === 0 && learnRequests.length === 0 && !showForm && !showLearnForm && (
            <div style={{ color: '#6b7280' }}>No skills in development. Use the buttons above to teach or add a skill.</div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminSkills;
